import { Link } from "@components/Link";
import { FooterSocialButtons } from "@components/layout/Footer";
import {
  MAX_PAGE_SECTION_WIDTH,
  PageSection,
  SECTION_WIDTH_VAR,
} from "@components/layout/PageSection";
import { cx } from "@emotion/css";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";

import {
  CauseCategory,
  CauseMetadata,
  TagNameByCauseCategory,
  ValidCauseCategory,
} from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  GetRoutePathParams,
} from "@every.org/common/src/helpers/clientRoutes";

import FooterBgLeft from "src/assets/illustrations/footer_bg_left.svg";
import FooterBgRight from "src/assets/illustrations/footer_bg_right.svg";
import { ReactComponent as FooterEasyIcon } from "src/assets/illustrations/footer_easy_icon.svg";
import { ReactComponent as FooterImpactfulIcon } from "src/assets/illustrations/footer_impactful_icon.svg";
import { ReactComponent as FooterTrustedIcon } from "src/assets/illustrations/footer_trusted_icon.svg";
import { ReactComponent as WordmarkSvg } from "src/assets/wordmark.svg";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight, textSizeCss } from "src/theme/text";
import { useUserLayer } from "src/utility/abtesting";

const benefits = [
  {
    title: "Easy",
    icon: <FooterEasyIcon />,
    description:
      "Modern fundraising made simple. Raise more by accepting all donation methods.",
  },
  {
    title: "Impactful",
    icon: <FooterImpactfulIcon />,
    description:
      "Every dollar counts. No platform fees means more money goes directly to your cause.",
  },
  {
    title: "Trusted",
    icon: <FooterTrustedIcon />,
    description:
      "Used by more than 8,000 nonprofits for secure and reliable donation processing.",
  },
];

const getLinkForCause = (
  entry: [string, { title: string }]
): [string, GetRoutePathParams] => {
  const [causeCategory, { title }] = entry;
  if (causeCategory === CauseCategory.UNKNOWN) {
    return ["Browse All Causes", { name: ClientRouteName.CAUSES }];
  }
  return [
    title,
    {
      name: ClientRouteName.NONPROFIT_OR_CAUSE,
      tokens: {
        nonprofitSlug:
          TagNameByCauseCategory[causeCategory as ValidCauseCategory],
      },
    },
  ];
};

type FooterColumn = {
  title: string;
  links: Array<[string, GetRoutePathParams]>;
};

const columns: FooterColumn[] = [
  {
    title: "Nonprofits",
    links: [
      ["Platform Overview", { name: ClientRouteName.NONPROFITS_LANDING }],
      ["Fundraising Pages", { name: ClientRouteName.FUNDRAISERS_LANDING }],
      ["Crypto Donations", { name: ClientRouteName.FUNDRAISE_CRYPTO }],
      ["Stock Donations", { name: ClientRouteName.FUNDRAISE_STOCK }],
      ["DAF Donations", { name: ClientRouteName.DAF_LANDING }],
      ["Fiscal Sponsors", { name: ClientRouteName.FISCAL_SPONSORS_LANDING }],
      [
        "Claim Your Profile",
        { name: ClientRouteName.NONPROFITS_ONBOARDING_SEARCH },
      ],
      ["Demo", { name: ClientRouteName.DEMO_LEAD }],
      ["Fees", { name: ClientRouteName.NONPROFITS_PRICING }],
    ],
  },
  {
    title: "Donors",
    links: [
      ["How It Works", { name: ClientRouteName.GIVERS_LANDING }],
      ["Search Nonprofits", { name: ClientRouteName.SEARCH_PAGE }],
      ["Sign Up", { name: ClientRouteName.SIGNUP }],
      ["Giving Crypto", { name: ClientRouteName.DONATE_CRYPTO_LANDING }],
      ["Gift Cards", { name: ClientRouteName.GIFT_CARD_LANDING }],
    ],
  },
  {
    title: "Top Causes",
    links: Object.entries(CauseMetadata).map(getLinkForCause),
  },
  {
    title: "Resources",
    links: [
      ["Support & FAQs", { name: ClientRouteName.SUPPORT }],
      ["Blog", { name: ClientRouteName.BLOG }],
      ["APIs for Developers", { name: ClientRouteName.API_LANDING }],
      ["Contact Us", { name: ClientRouteName.CONTACT }],
    ],
  },
  {
    title: "About",
    links: [
      ["About Us", { name: ClientRouteName.ABOUT }],
      ["Media", { name: ClientRouteName.PRESS }],
      ["Brand", { name: ClientRouteName.BRAND }],
      ["Privacy", { name: ClientRouteName.PRIVACY_POLICY }],
      ["Terms", { name: ClientRouteName.TERMS_OF_SERVICE }],
    ],
  },
];

// #region styles
const StyledWordmark = styled(WordmarkSvg)`
  .every {
    fill: var(${colorCssVars.text.body});
  }
  .dotorg {
    fill: var(${colorCssVars.logo});
  }
`;

const StyledFooter = styled.footer`
  --bgsize: 300px;

  background: url(${FooterBgLeft}) no-repeat fixed calc(-1 * var(--bgsize) / 2),
    url(${FooterBgRight}) no-repeat fixed calc(100% + var(--bgsize) / 2),
    linear-gradient(0deg, rgba(1, 134, 105, 0) 0%, rgba(1, 134, 105, 0.1) 50%);

  background-size: var(--bgsize), var(--bgsize), 100%;

  ${SECTION_WIDTH_VAR}: ${MAX_PAGE_SECTION_WIDTH};

  .footer-top-extended {
    padding-top: 4.5rem;
    background: linear-gradient(180deg, rgba(46, 52, 52, 0) 0%, #2e3434 50%);
  }

  .page-section-content {
    padding-right: ${spacing.l};
    padding-left: ${spacing.l};
  }

  .footer-extended-text {
    text-align: center;
    ${verticalStackCss.m};
    align-items: center;
  }

  .footer-extended-header {
    ${textSizeCss.xl};
    padding: 0 ${spacing.s};
  }

  .footer-extended-copy {
    max-width: 600px;
  }

  .footer-extended-highlights {
    margin-top: 3rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3.5rem;
    padding-bottom: 4rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .footer-extended-highlight {
    max-width: 320px;
    text-align: center;
    ${verticalStackCss.xs};
    align-items: center;
  }

  .footer-extended-highlight-heading {
    ${textSizeCss.l};
    font-weight: ${FontWeight.BOLD};
    color: var(${colorCssVars.accent.large});
    ${horizontalStackCss.xs};
    align-items: center;
  }

  .footer-extended-columns {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding: 3rem 0 4rem;
    display: flex;
    flex-direction: column;
    gap: ${spacing.l} 0;
  }

  .footer-extended-column {
    display: flex;
    flex-direction: column;
    gap: ${spacing.xxs};
  }

  .footer-extended-h5 {
    ${textSizeCss.s};
    font-weight: ${FontWeight.BOLD};
  }

  .footer-extended-link {
    font-weight: ${FontWeight.MEDIUM};
    color: var(${colorCssVars.text.secondary});
  }

  .footer-bottom-extended {
    padding: ${spacing.l} 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: ${spacing.m};
  }

  .footer-org {
    display: flex;
    align-items: center;
    gap: ${spacing.s};
  }

  .tiny-text {
    ${textSizeCss.xxs};
    color: var(${colorCssVars.text.secondary});
  }

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      --bgsize: 450px;

      .footer-top-extended {
        padding-top: 6rem;
      }

      .page-section-content {
        padding-right: ${spacing.xxl};
        padding-left: ${spacing.xxl};
      }

      .footer-extended-highlights {
        flex-direction: row;
        justify-content: center;
        align-items: flex-start;
        padding-bottom: 6rem;
        gap: ${spacing.s};
      }

      .footer-extended-header {
        ${textSizeCss.xxl};
        padding: 0;
      }

      .footer-extended-copy {
        ${textSizeCss.l};
        max-width: 640px;
      }

      .footer-extended-h5 {
        margin-bottom: ${spacing.xxs};
      }

      .footer-extended-columns {
        flex-direction: row;
        flex-wrap: wrap;
        padding: 3rem 0 4rem;
        gap: ${spacing.xxxl} 0;
      }

      .footer-extended-column {
        flex: 0 1 33%;
      }
    `,
  })}

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      --bgsize: 600px;
      .footer-extended-highlights {
        gap: ${spacing.l};
        padding-bottom: 5.5rem;
      }

      .footer-extended-highlight {
        padding: 0 ${spacing.l};
        max-width: 360px;
      }

      .footer-extended-h5 {
        margin-bottom: ${spacing.s};
      }

      .footer-extended-columns {
        justify-content: space-between;
        flex-wrap: nowrap;
        flex: 0 1 20%;
        padding: 3rem 0 6rem;
      }
    `,
  })}

  .footer-extended-columns.no-divider {
    border-top: 0;
    padding-top: 0;
  }
`;
// #endregion

/**
 * @param hideValueProp omits the heading/copy and the Easy/Impactful/Trusted
 * block, for the homepage section reorder experiment treatment where that
 * content is rendered above the feed by `LandingValuePropSection` instead.
 */
export function ExtendedFooter({
  hideValueProp,
}: {
  hideValueProp?: boolean;
} = {}) {
  const heading = useUserLayer("new_landing_page_layer", false)
    .get<string>("heading", "Easier giving. Greater&nbsp;good.")
    .replace(/&nbsp;/g, "\u00A0");

  return (
    <StyledFooter>
      <div className="footer-top-extended">
        <PageSection contentClassName="page-section-content">
          <div className="footer-container-extended">
            {!hideValueProp && (
              <React.Fragment>
                <div className="footer-extended-text">
                  <h2 className="footer-extended-header">{heading}</h2>
                  <p className="footer-extended-copy">
                    Every.org simplifies online giving for nonprofits and donors
                    in order to accelerate social impact around the world.
                  </p>
                </div>
                <div className="footer-extended-highlights">
                  {benefits.map(({ icon, title, description }) => (
                    <div className="footer-extended-highlight" key={title}>
                      <div className="footer-extended-highlight-heading">
                        {icon}
                        <span className="footer-extended-h4">{title}</span>
                      </div>
                      <p className="footer-extended-highlight-desc">
                        {description}
                      </p>
                    </div>
                  ))}
                </div>
              </React.Fragment>
            )}

            <div
              className={cx("footer-extended-columns", {
                "no-divider": hideValueProp,
              })}
            >
              {columns.map(({ title, links }) => (
                <div className="footer-extended-column" key={title}>
                  <h5 className="footer-extended-h5">{title}</h5>
                  {links.map(([text, routeSpecs]) => (
                    <Link
                      data-tname={text.toLowerCase().replace(/ /g, "-")}
                      key={text}
                      to={getRoutePath(routeSpecs)}
                      className="footer-extended-link"
                    >
                      {text}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </PageSection>
      </div>
      <PageSection contentClassName="page-section-content">
        <div className="footer-bottom-extended">
          <div className="footer-org">
            <StyledWordmark className="footer-logo" />
            <span className="tiny-text">
              &#169; {new Date().getFullYear().toString()} Every.org 501(c)(3)
              EIN 61-1913297
            </span>
          </div>
          <FooterSocialButtons />
        </div>
      </PageSection>
    </StyledFooter>
  );
}
